import { useEffect } from "react";
import styled from "styled-components";
import * as amplitude from "@amplitude/analytics-browser";

import ProgressHeader from "./ProgressHeader";
import { BridgeScreenConfig, BRIDGE_CONTENT } from "./bridgeContent";

type BridgeScreenProps = {
  onContinue: () => void;
};

const HeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
  max-width: calc(80 * var(--base-unit, 8px));
  margin: 0 auto;
  padding: 0 24px 0;
  box-sizing: border-box;
`;

const SectionLabelContainer = styled.div`
  padding: 10px 16px;
  background: linear-gradient(270deg, #E7F0FF 3.83%, #FFFFFF 100%);
  border-radius: 100px;
  margin-top: 24px;
  margin-bottom: 16px;

  @media screen and (min-width: 695px) {
    margin-top: 64px;
  }
`;

const SectionLabel = styled.div`
  font-family: Oswald, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: 0px;
  text-align: center;
  text-transform: uppercase;
  color: #0B6DFF;

  @media screen and (min-width: 695px) {
    font-size: 16px;
  }
`;

const Headline = styled.h1`
  font-family: Lato, sans-serif;
  font-weight: 800;
  font-size: 28px;
  line-height: 1.2;
  text-align: center;
  color: #000;
  margin: 0 0 12px;

  @media screen and (min-width: 695px) {
    font-size: 36px;
  }
`;

const HighlightedLine = styled.span`
  display: block;
`;

const HighlightedText = styled.span`
  color: #0B6DFF;
  font-weight: 800;
  background-image: linear-gradient(transparent 85%, rgba(11, 109, 255, 0.18) 85%);
  background-repeat: no-repeat;
  background-size: 100% 100%;
`;

const Subheadline = styled.p`
  font-family: Lato, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  color: #4a4a4a;
  margin: 0 auto 28px;
  max-width: 400px;
  padding: 0 36px;

  @media screen and (min-width: 695px) {
    font-size: 18px;
  }
`;

const PainPointsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 400px;
  margin-bottom: 20px;
`;

const PainPointCard = styled.div<{ variant: "negative" | "positive" }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: ${(p) => p.variant === "negative" ? "#FEF0EC" : "#ECFAEC"};
  font-family: Lato, sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 140%;
  color: #1a1a1a;

  @media screen and (min-width: 695px) {
    font-size: 16px;
  }
`;

const Emoji = styled.span`
  font-size: 20px;
  flex-shrink: 0;
`;

const SocialProofCard = styled.div<{ position?: 'inline' | 'bottom' }>`
  display: ${(p) => p.position === 'bottom' ? 'none' : 'block'};
  width: 100%;
  max-width: 340px;
  box-sizing: border-box;
  padding: 0 20px;
  text-align: center;
  font-family: Lato, sans-serif;
  font-size: 13px;
  font-style: italic;
  line-height: 1.6;
  color: #8a8a9a;

  strong { color: #0B6DFF; font-weight: 700; }

  @media screen and (max-height: 620px) {
    display: ${(p) => p.position === 'inline' ? 'none' : 'block'};
    margin-top: ${(p) => p.position === 'bottom' ? '16px' : '0'};
  }
`;

const SocialProofEmoji = styled.span`
  font-size: 20px;
  flex-shrink: 0;
  margin-right: 4px;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: calc(80 * var(--base-unit, 8px));
  margin: 0 auto;
  padding: 0 24px 32px;
  box-sizing: border-box;

  @media screen and (max-height: 620px) {
    padding-bottom: 16px;
  }
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 400px;
  margin-top: 16px;
`;

const CTAButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background-color: #0b6dff;
  font-family: Lato, sans-serif;
  font-weight: 510;
  font-size: 16px;
  line-height: 120%;
  color: #fff;
  cursor: pointer;
  padding: 12px 24px;
  border-radius: 50px;
  width: 100%;

  svg {
    margin-left: 10px;
  }

  @media screen and (min-width: 695px) {
    max-width: 400px;
  }
`;

const BridgeScreen: React.FC<BridgeScreenProps> = ({ onContinue }) => {
  const utmAngle = localStorage.getItem("utm_angle") || "";
  const config: BridgeScreenConfig | undefined = BRIDGE_CONTENT[utmAngle];

  useEffect(() => {
    if (!config) return;
    amplitude.track("bridge_screen_viewed", {
      utm_angle: utmAngle,
      experiment_group: "treatment",
    });
  }, []);

  useEffect(() => {
    if (!config) {
      onContinue();
    }
  }, [config, onContinue]);

  if (!config) {
    return null;
  }

  const handleCTA = () => {
    amplitude.track("bridge_screen_cta_clicked", {
      utm_angle: utmAngle,
    });
    onContinue();
  };

  return (
    <>
      <HeaderContainer>
        <ProgressHeader progress={0} title="Getting Started" />
      </HeaderContainer>
      <Content>
        <SectionLabelContainer>
          <SectionLabel>{config.sectionLabel}</SectionLabel>
        </SectionLabelContainer>
        <Headline>
          <span style={{ whiteSpace: 'nowrap' }}>{config.headline.prefix}</span>
          <HighlightedLine>
            <HighlightedText>{config.headline.highlighted}</HighlightedText>
          </HighlightedLine>
        </Headline>
        <Subheadline dangerouslySetInnerHTML={{ __html: config.subheadline }} />
        <PainPointsContainer>
          {config.painPoints.map((point, i) => (
            <PainPointCard key={i} variant={point.variant}>
              <Emoji>{point.emoji}</Emoji>
              <span>{point.text}</span>
            </PainPointCard>
          ))}
        </PainPointsContainer>
        <SocialProofCard position="inline">
          <SocialProofEmoji>{config.socialProof.emoji}</SocialProofEmoji>
          <span>
            <strong>{config.socialProof.count}</strong>{' '}{config.socialProof.text}
          </span>
        </SocialProofCard>
      </Content>
      <BottomSection>
        <FooterContainer>
          <CTAButton onClick={handleCTA}>
            <span>{config.cta}</span>
            <svg
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.11876 21.0098C8.60876 21.4998 9.39876 21.4998 9.88876 21.0098L18.1988 12.6998C18.5888 12.3098 18.5888 11.6798 18.1988 11.2898L9.88876 2.9798C9.39876 2.4898 8.60876 2.4898 8.11876 2.9798C7.62876 3.4698 7.62876 4.2598 8.11876 4.7498L15.3588 11.9998L8.10876 19.2498C7.62876 19.7298 7.62876 20.5298 8.11876 21.0098Z"
                fill="white"
              />
            </svg>
          </CTAButton>
        </FooterContainer>
        <SocialProofCard position="bottom">
          <SocialProofEmoji>{config.socialProof.emoji}</SocialProofEmoji>
          <span>
            <strong>{config.socialProof.count}</strong>{' '}{config.socialProof.text}
          </span>
        </SocialProofCard>
      </BottomSection>
    </>
  );
};

export default BridgeScreen;
